import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Button, Flex, Form, InputNumber, List, Modal, Progress, Radio, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useState } from "react";
import { ApiError, api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type MessageKey } from "@/i18n";
import { planAllows } from "@/lib/plan";
import { useConnections } from "../channels/hooks";
import { PlatformLabel } from "../channels/PlatformBadge";
import { useRefreshMapping, type PlatformSKU } from "./mapping";

type Rule = Schemas["PriceRule"];
type PreviewItem = { channelSkuId: string; skuCode: string; platform: Schemas["ChannelPlatformName"]; shopName: string; listingName: string; variant: string; oldPrice: number | null; newPrice: number | null; problem?: string };

/** An error of a price change: a price under the SKU's minimum says so. */
export function usePriceErrorText() {
  const { t } = useI18n();
  const errorText = useErrorText();
  return (err: unknown) => (err instanceof ApiError && Object.values(err.fields).includes("below_floor") ? t("price.belowFloor") : errorText(err));
}

/** The sync state of a platform SKU's last price change. */
export function PriceSyncTag({ p }: { p: Pick<PlatformSKU, "priceSync"> }) {
  const { t, formatMoney } = useI18n();
  const s = p.priceSync;
  if (!s) return null;
  if (s.status === "pending") return <Tag color="processing">{t("price.sync.pendingTo", { price: formatMoney(s.newPrice, "IDR") })}</Tag>;
  if (s.status === "failed")
    return (
      <Tooltip title={s.error}>
        <Tag color="error">{t("price.sync.failed")}</Tag>
      </Tooltip>
    );
  return <Tag color="success">{t("price.sync.done")}</Tag>;
}

/**
 * Follows one price batch until the marketplaces answered for every change,
 * and shows the outcome ("ringkasan berhasil/gagal").
 */
export function BatchSummary({ batchId, onClose }: { batchId: string | null; onClose: () => void }) {
  const { t } = useI18n();
  const refresh = useRefreshMapping();
  const batch = useQuery({
    queryKey: ["prices", "batch", batchId],
    queryFn: () => unwrap(api.GET("/v1/prices/batches/{batchID}", { params: { path: { batchID: batchId! } } })),
    enabled: batchId !== null,
    refetchInterval: (q) => ((q.state.data?.pending ?? 1) > 0 ? 1500 : false),
  });
  const b = batch.data;
  const finished = b ? b.total - b.pending : 0;
  return (
    <Modal
      open={batchId !== null}
      title={t("price.summaryTitle")}
      onCancel={() => {
        refresh();
        onClose();
      }}
      footer={
        <Button
          type="primary"
          onClick={() => {
            refresh();
            onClose();
          }}
        >
          {t("common.close")}
        </Button>
      }
    >
      {b && (
        <Space orientation="vertical" style={{ width: "100%" }}>
          <Progress percent={Math.round((finished / Math.max(b.total, 1)) * 100)} status={b.failed > 0 && b.pending === 0 ? "exception" : undefined} />
          {b.pending > 0 ? (
            <Typography.Text type="secondary">{t("price.summarySending", { done: finished, total: b.total })}</Typography.Text>
          ) : (
            <Alert type={b.failed > 0 ? "warning" : "success"} showIcon title={t("price.summaryDone", { done: b.done, failed: b.failed })} />
          )}
          {b.superseded > 0 && <Typography.Text type="secondary">{t("price.summarySuperseded", { n: b.superseded })}</Typography.Text>}
          {b.failures.length > 0 && (
            <List
              size="small"
              dataSource={b.failures}
              renderItem={(f) => (
                <List.Item>
                  <Space orientation="vertical" size={0}>
                    <span>
                      <PlatformLabel platform={f.platform} /> · {f.shopName} · {[f.listingName, f.variant].filter(Boolean).join(" ")}
                    </span>
                    <Typography.Text type="danger" style={{ fontSize: 12 }}>
                      {f.error}
                    </Typography.Text>
                  </Space>
                </List.Item>
              )}
            />
          )}
        </Space>
      )}
    </Modal>
  );
}

type RuleForm = { mode: Rule["mode"]; value: number; roundTo: NonNullable<Rule["roundTo"]>; roundMode: "nearest" | "up" | "down"; stores: string[] };

/** "Ubah harga massal": a rule over the platform SKUs of the chosen SKUs, previewed before it is applied. */
export function BulkPriceModal({ skuIds, onClose }: { skuIds: string[] | null; onClose: () => void }) {
  const { t, formatMoney } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const connections = useConnections();
  const { me, can } = useAuth();
  // "Harga saran" shows the cost of goods: finance only, not on Free (Plan §6.20).
  const suggestedAllowed = can("finance.view") && planAllows(me?.workspace?.plan, "finance");
  const [form] = Form.useForm<RuleForm>();
  const mode = Form.useWatch("mode", form) ?? "percent";
  const [preview, setPreview] = useState<PreviewItem[] | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);

  // Suggested and base take no value: each item's comes from its SKU and store.
  const valueless = mode === "suggested" || mode === "base";
  const ruleOf = (v: RuleForm): Rule =>
    v.mode === "suggested"
      ? { mode: v.mode, value: 0, roundTo: v.roundTo }
      : { mode: v.mode, value: v.mode === "base" ? 0 : v.value, roundTo: v.roundTo, roundMode: v.roundMode };
  const runPreview = useMutation({
    mutationFn: (v: RuleForm) => unwrap(api.POST("/v1/prices/preview", { body: { skuIds: skuIds!, connectionIds: v.stores, rule: ruleOf(v) } })),
    onSuccess: ({ items }) => setPreview(items as PreviewItem[]),
    onError: (err) => void message.error(errorText(err)),
  });
  const applicable = (preview ?? []).filter((i) => i.newPrice !== null && i.newPrice !== i.oldPrice);
  const apply = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/prices", { body: { items: applicable.map((i) => ({ channelSkuId: i.channelSkuId, price: i.newPrice! })) } })),
    onSuccess: ({ batchId }) => setBatchId(batchId),
    onError: (err) => void message.error(errorText(err)),
  });
  const close = () => {
    setPreview(null);
    setBatchId(null);
    form.resetFields();
    onClose();
  };

  return (
    <>
      <Modal
        open={skuIds !== null && batchId === null}
        width={920}
        title={t("price.bulkTitle", { n: skuIds?.length ?? 0 })}
        onCancel={close}
        destroyOnHidden
        footer={
          <Flex justify="end" gap={8}>
            <Button onClick={close}>{t("common.cancel")}</Button>
            <Button loading={runPreview.isPending} onClick={() => form.submit()}>
              {t("price.preview")}
            </Button>
            <Button type="primary" disabled={applicable.length === 0} loading={apply.isPending} onClick={() => apply.mutate()}>
              {t("price.apply", { n: applicable.length })}
            </Button>
          </Flex>
        }
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ mode: "percent", value: -10, roundTo: 100, roundMode: "nearest", stores: [] }}
          onValuesChange={() => setPreview(null)}
          onFinish={(v) => runPreview.mutate(v)}
        >
          <Flex gap={16} wrap>
            <Form.Item name="mode" label={t("price.rule")}>
              <Radio.Group
                optionType="button"
                options={(["percent", "amount", "set", "base", ...(suggestedAllowed ? (["suggested"] as const) : [])] as const).map((m) => ({
                  value: m,
                  label: t(`price.mode.${m}`),
                }))}
              />
            </Form.Item>
            {!valueless && (
              <Form.Item name="value" label={t(`price.value.${mode}` as MessageKey)} rules={[{ required: true, message: t("field.required") }]}>
                <InputNumber style={{ width: 180 }} min={mode === "set" ? 100 : undefined} precision={mode === "percent" ? 2 : 0} />
              </Form.Item>
            )}
            <Form.Item name="roundTo" label={t("price.round")}>
              <Select
                style={{ width: 180 }}
                options={[
                  { value: 1, label: t("price.round.none") },
                  ...[100, 500, 1000].map((n) => ({ value: n, label: t("price.round.to", { n: n.toLocaleString("id-ID") }) })),
                ]}
              />
            </Form.Item>
            {mode !== "suggested" && (
              <Form.Item name="roundMode" label=" ">
                <Select style={{ width: 130 }} options={(["nearest", "up", "down"] as const).map((m) => ({ value: m, label: t(`price.roundMode.${m}`) }))} />
              </Form.Item>
            )}
          </Flex>
          {mode === "suggested" && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("price.suggestedHelp")} />}
          {mode === "base" && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("price.baseHelp")} />}
          <Form.Item name="stores" label={t("price.stores")}>
            <Select
              mode="multiple"
              allowClear
              placeholder={t("price.storesAll")}
              options={(connections.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
        </Form>
        {preview && (
          <Table<PreviewItem>
            size="small"
            rowKey="channelSkuId"
            dataSource={preview}
            pagination={false}
            scroll={{ y: 360 }}
            locale={{ emptyText: t("price.previewEmpty") }}
            columns={[
              { title: t("catalog.code"), dataIndex: "skuCode", width: 140, render: (v: string) => <span className="z-num">{v}</span> },
              {
                title: t("orders.store"),
                width: 190,
                render: (_, i) => (
                  <Space orientation="vertical" size={0}>
                    <PlatformLabel platform={i.platform} />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {i.shopName}
                    </Typography.Text>
                  </Space>
                ),
              },
              { title: t("catalog.platform.product"), render: (_, i) => [i.listingName, i.variant].filter(Boolean).join(" · ") },
              { title: t("price.current"), dataIndex: "oldPrice", width: 120, align: "right", render: (v: number | null) => <span className="z-num">{v === null ? "—" : formatMoney(v, "IDR")}</span> },
              {
                title: t("price.new"),
                width: 210,
                align: "right",
                render: (_, i) => (
                  <Space orientation="vertical" size={0} align="end">
                    <Typography.Text strong className="z-num">
                      {i.newPrice === null ? "—" : formatMoney(i.newPrice, "IDR")}
                    </Typography.Text>
                    {i.problem && (
                      <Tag color={i.problem === "big_change" ? "warning" : i.problem === "below_break_even" || i.problem === "below_floor" ? "error" : "default"}>
                        {t(`price.problem.${i.problem}` as MessageKey)}
                      </Tag>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Modal>
      <BatchSummary batchId={batchId} onClose={close} />
    </>
  );
}
